// src/features/TeamChat/components/TeamLauncher.tsx
import { useEffect, useRef } from 'react'
import type { MouseEvent, PointerEvent } from 'react'

import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { BellOffIcon, LauncherIcon } from './TeamIcons'

type Props = {
  openCount: number
  hasUnread: boolean
  muted: boolean
  takeFocus: boolean
  onFocused: () => void
  onOpen: (e: MouseEvent<HTMLButtonElement>) => void
  onDragStart: (e: PointerEvent<HTMLElement>) => void
}

const launcherLabel = (openCount: number, hasUnread: boolean, muted: boolean) =>
  [
    TEAM_COPY.OPEN_LABEL,
    openCount > 0 && TEAM_COPY.OPEN_COUNT(openCount),
    hasUnread && TEAM_COPY.UNREAD,
    muted && TEAM_COPY.SOUNDS_OFF,
  ]
    .filter(Boolean)
    .join(', ')

// A bare button: the launcher is a floating, draggable pill, not one of the app's form buttons.
export const TeamLauncher = ({
  openCount,
  hasUnread,
  muted,
  takeFocus,
  onFocused,
  onOpen,
  onDragStart,
}: Props) => {
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!takeFocus) return
    ref.current?.focus()
    onFocused()
  }, [takeFocus, onFocused])

  return (
    <button
      ref={ref}
      type='button'
      className={styles.launcher}
      aria-label={launcherLabel(openCount, hasUnread, muted)}
      onPointerDown={onDragStart}
      onClick={onOpen}>
      <LauncherIcon />
      <span>{TEAM_COPY.TITLE}</span>
      {muted && <BellOffIcon size={16} />}
      {openCount > 0 && (
        <span key={openCount} className={styles.launcherCount}>
          {openCount}
        </span>
      )}
      {hasUnread && <span className={styles.unreadDot} />}
    </button>
  )
}
